import { useState, useCallback } from "react";
import { ZodSchema } from "zod";
import { useForgotPasswordMutation } from "./useForgotPasswordMutation";
import { baseForgotPasswordSchema } from "../auth.schemas";

export type ForgotPasswordFormData = {
  email: string;
};

export type UseForgotPasswordProps = {
  /** Success callback */
  onSuccess?: (data: any) => void;
  /** Error callback */
  onError?: () => void;
  /** Custom schema (defaults to baseForgotPasswordSchema) */
  schema?: ZodSchema;
};

export type UseForgotPasswordReturn = {
  /** Forgot password mutation for form submission */
  forgotPasswordMutation: ReturnType<typeof useForgotPasswordMutation>;
  /** Whether the email was sent successfully */
  isSuccess: boolean;
  /** Reset the success state (for resending) */
  resetSuccess: () => void;
  /** Schema for form validation */
  schema: ZodSchema;
  /** Default values for form */
  defaultValues: ForgotPasswordFormData;
};

/**
 * Composite hook for forgot password functionality.
 * Manages mutation and success state.
 *
 * Usage:
 * ```tsx
 * const {
 *   forgotPasswordMutation,
 *   isSuccess,
 *   resetSuccess,
 *   schema,
 *   defaultValues,
 * } = useForgotPassword({
 *   onSuccess: () => console.log('Email sent'),
 *   onError: () => toast.error('Failed to send email'),
 * });
 *
 * const { form, handleSubmit } = useForm({
 *   schema,
 *   onSubmit: async (data) => {
 *     await forgotPasswordMutation.mutateAsync(data);
 *   },
 *   defaultValues,
 * });
 * ```
 */
export function useForgotPassword({
  onSuccess,
  onError,
  schema = baseForgotPasswordSchema,
}: UseForgotPasswordProps = {}): UseForgotPasswordReturn {
  const [isSuccess, setIsSuccess] = useState(false);

  // Forgot password mutation with success tracking
  const forgotPasswordMutation = useForgotPasswordMutation({
    onSuccess: (data: any) => {
      setIsSuccess(true);
      onSuccess?.(data);
    },
    onError,
    schema,
  });

  const resetSuccess = useCallback(() => {
    setIsSuccess(false);
  }, []);

  const defaultValues: ForgotPasswordFormData = {
    email: "",
  };

  return {
    forgotPasswordMutation,
    isSuccess,
    resetSuccess,
    schema,
    defaultValues,
  };
}

export default useForgotPassword;

